// スポット入札のまとめて承認 (AC-BSP-118) の、 通常入札・ブロック入札に共通する純関数。
// 案件 (通常入札 = 申請セット、 ブロック入札 = グループ) を `BulkApprovalCase` に正規化し、
// 選択・合計・再確認の件数・失敗理由の区分はこの型だけを見て決める。
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { BiddingBulkApprovalFailureReason } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { JEPX_AREA_CODES } from '~/models/area'

import type { ApproverIdentity } from './approverIdentity'
import type { SpotAreaBreakdownRow } from './spotApprovalDecision'

/** まとめて承認の対象 1 件 (申請セット / ブロックグループ) の表示・検証用の写し。 */
export type BulkApprovalCase = {
  id: string
  /** 楽観ロック基準値 (= 画面が読んだ案件の updatedAt。 proto Timestamp のまま送る)。 */
  expectedUpdatedAt: Timestamp | undefined
  /** 申請者の member ID (表示名は描画側が引く)。 */
  requestedBy: string | undefined
  /** 案件名 (例: 「申請セット 1」「リンクブロック 2」)。 */
  title: string
  /** 案件名の下に添える内容 (札数 / コマ範囲)。 */
  detail: string
  areaRows: SpotAreaBreakdownRow[]
  buyMw: number
  sellMw: number
  /** 入札制限等の違反明細 (AC-BSP-097 の UX 事前チェック。 承認可否の最終判定は backend)。 */
  violationSections: ViolationSection[]
  /**
   * 入札制限・相手タブの数量を取得できず判定できない節。 違反とは分けて持ち、 選択中に
   * 1 件でもあれば確定を止める (単件の承認と同じ fail-closed)。
   */
  unverifiedSections: ViolationSection[]
  /** 確定を止めない注意 (売買逆転)。 */
  warningSections: ViolationSection[]
  /** 承認者非該当・自己承認の識別 (AC-BSP-098)。 */
  identity: ApproverIdentity
}

/** 明細を持つ節の明細行数の合計。 */
export const countViolationDetails = (
  sections: readonly ViolationSection[],
): number => sections.reduce((sum, s) => sum + s.details.length, 0)

/** 案件の入札制限違反が見込まれるか。 */
export const hasBulkViolation = (c: BulkApprovalCase): boolean =>
  c.violationSections.some((s) => s.details.length > 0)

/** 案件に、 入札制限を判定できていない節があるか。 */
export const hasBulkUnverified = (c: BulkApprovalCase): boolean =>
  c.unverifiedSections.some((s) => s.details.length > 0)

/**
 * 案件群の判定できない節を、 見出しで重複を除いて返す (同じ理由は案件の数だけ並べない)。
 * 並びは最初に現れた順。
 */
export const collectUnverifiedSections = (
  cases: readonly BulkApprovalCase[],
): ViolationSection[] => {
  const byTitle = new Map<string, ViolationSection>()
  for (const c of cases) {
    for (const s of c.unverifiedSections) {
      if (s.details.length > 0 && !byTitle.has(s.title)) byTitle.set(s.title, s)
    }
  }
  return [...byTitle.values()]
}

/** 選ばれた案件を、 案件の並び (= 画面の並び順) のまま返す。 */
export const pickSelectedCases = (
  cases: readonly BulkApprovalCase[],
  deselectedIds: ReadonlySet<string>,
): BulkApprovalCase[] => cases.filter((c) => !deselectedIds.has(c.id))

/** 選択中の案件の合計 (件数 / 買・売 MW / エリア別内訳)。 */
export type BulkSelectionSummary = {
  count: number
  buyMw: number
  sellMw: number
  areaRows: SpotAreaBreakdownRow[]
}

/**
 * 選択中の案件の件数・買売合計・エリア別内訳。 数量は MW の単純合算 (AC-BSP-053、
 * 単件の確認 modal の `summarizeSpotBidRows` と同じ)。 エリアの並びは `JEPX_AREA_CODES`
 * (北海道 → 九州) で、 札が無いエリアは行を作らない。
 */
export const summarizeBulkSelection = (
  selected: readonly BulkApprovalCase[],
): BulkSelectionSummary => {
  const byArea = new Map<number, SpotAreaBreakdownRow>()
  let buyMw = 0
  let sellMw = 0
  for (const c of selected) {
    buyMw += c.buyMw
    sellMw += c.sellMw
    for (const row of c.areaRows) {
      const merged = byArea.get(row.areaCode)
      byArea.set(
        row.areaCode,
        merged === undefined
          ? { ...row }
          : {
              ...merged,
              count: merged.count + row.count,
              buyMw: merged.buyMw + row.buyMw,
              sellMw: merged.sellMw + row.sellMw,
            },
      )
    }
  }
  return {
    count: selected.length,
    buyMw,
    sellMw,
    areaRows: JEPX_AREA_CODES.flatMap((code) => {
      const row = byArea.get(code)
      return row === undefined ? [] : [row]
    }),
  }
}

/** 選択中の案件のうち、 AC-BSP-098 の再確認が要るものの件数。 */
export type BulkApproverNoticeCounts = {
  /** 当該承認段階の承認者に該当しない案件。 */
  notApprover: number
  /** 自分が申請した案件 (自己承認)。 */
  selfApproval: number
}

export const countApproverNotices = (
  selected: readonly BulkApprovalCase[],
): BulkApproverNoticeCounts => ({
  notApprover: selected.filter((c) => c.identity.isNotApprover).length,
  selfApproval: selected.filter((c) => c.identity.isSelfApproval).length,
})

/** 再確認 (AC-BSP-098 (b)) を 1 回経る必要があるか。 */
export const needsReconfirm = (counts: BulkApproverNoticeCounts): boolean =>
  counts.notApprover + counts.selfApproval > 0

/** 画面が理由の出し分けに使う、 失敗理由の区分。 */
export type BulkFailureKind =
  | 'violation'
  | 'updatedByOther'
  | 'canceledOrDeleted'
  | 'gateClosure'
  | 'other'

/**
 * backend の失敗理由 (`BiddingBulkApprovalFailureReason`) を画面の区分に写す。 全 enum 値を
 * 網羅する (値が増えるとここが compile error になる)。 UNSPECIFIED と未知の値は other に倒し、
 * 理由を空欄にしない。
 */
export const classifyBulkFailure = (
  reason: BiddingBulkApprovalFailureReason,
): BulkFailureKind => {
  switch (reason) {
    case BiddingBulkApprovalFailureReason.BID_LIMIT_VIOLATION:
      return 'violation'
    case BiddingBulkApprovalFailureReason.UPDATED_BY_OTHER:
      return 'updatedByOther'
    case BiddingBulkApprovalFailureReason.CANCELED_OR_DELETED:
      return 'canceledOrDeleted'
    case BiddingBulkApprovalFailureReason.GATE_CLOSURE:
      return 'gateClosure'
    case BiddingBulkApprovalFailureReason.OTHER:
    case BiddingBulkApprovalFailureReason.UNSPECIFIED:
      return 'other'
    default:
      return 'other'
  }
}

/** 通常入札・ブロック入札の応答を揃えた、 案件 1 件の承認結果。 */
export type BulkItemResult =
  | { id: string; kind: 'approved' }
  | {
      id: string
      kind: 'failed'
      reason: BiddingBulkApprovalFailureReason
      /** reason = BID_LIMIT_VIOLATION の詳細を、 単件エラーと同じ文言にしたもの (無ければ undefined)。 */
      writeBlockedMessage: string | undefined
    }

/** 応答の案件 1 件ぶん (通常入札・ブロック入札の応答型に共通する形)。 */
export type BulkWireResult<W extends number> = {
  id: string
  outcome:
    | { case: 'approved'; value: unknown }
    | {
        case: 'failure'
        value: {
          reason: BiddingBulkApprovalFailureReason
          writeBlockedReason: W
        }
      }
    | { case: undefined; value?: undefined }
}

/**
 * 応答の案件 1 件を `BulkItemResult` に揃える。 outcome が欠けた応答は other の失敗にする
 * (= 承認できたと誤認させない)。
 */
export const normalizeBulkResult = <W extends number>(
  r: BulkWireResult<W>,
  /** 入札制限違反の詳細 (`write_blocked_reason`) を単件エラーと同じ文言にする。 */
  writeBlockedMessageOf: (reason: W) => string | undefined,
): BulkItemResult => {
  switch (r.outcome.case) {
    case 'approved':
      return { id: r.id, kind: 'approved' }
    case 'failure':
      return {
        id: r.id,
        kind: 'failed',
        reason: r.outcome.value.reason,
        writeBlockedMessage: writeBlockedMessageOf(
          r.outcome.value.writeBlockedReason,
        ),
      }
    case undefined:
      return {
        id: r.id,
        kind: 'failed',
        reason: BiddingBulkApprovalFailureReason.OTHER,
        writeBlockedMessage: undefined,
      }
  }
}

/** 結果表示の失敗 1 件 (案件と、 理由を表す文の並び)。 */
export type BulkFailureRow = {
  id: string
  title: string
  requestedBy: string | undefined
  /** 理由の文。 違反サマリの時は明細行ごとに 1 文。 空にはしない。 */
  lines: string[]
  /** 違反サマリで省いた明細行の数。 */
  hiddenCount: number
}

/** 実行結果。 */
export type BulkApprovalOutcome =
  | {
      kind: 'done'
      approvedCount: number
      failures: BulkFailureRow[]
    }
  | {
      /** RPC が error になった。 承認された件数は断定できない。 */
      kind: 'rpcError'
      message: string
    }

/** 違反サマリに出す明細行の上限 (これを超える分は「…他 N 件」にまとめる)。 */
export const MAX_FAILURE_DETAIL_LINES = 3

/** 結果表示の理由の文 (呼び出し側が t() 済みで渡す。 通常入札 / ブロック入札で文言が違う)。 */
export type BulkFailureMessages = {
  updatedByOther: string
  canceledOrDeleted: string
  gateClosure: string
  other: string
  /**
   * 入札制限違反なのに、 画面の事前チェックでは違反が 0 件で、 `write_blocked_reason` にも専用
   * 文言が無い時の汎用の文。 理由を空欄にしないために持つ。
   */
  violationFallback: string
}

/**
 * 送った案件の結果を、 承認件数と失敗の一覧にまとめる。
 *
 * - 結果の無い案件 (応答が契約どおりでない時) は other の失敗として残す (= 静かに消さない)
 * - 入札制限違反は、 確定時点の事前チェックで見えた違反サマリを出す。 見えなければ
 *   `violationFallback` の文
 */
export const summarizeBulkResults = (
  sent: readonly BulkApprovalCase[],
  results: readonly BulkItemResult[],
  messages: BulkFailureMessages,
): { approvedCount: number; failures: BulkFailureRow[] } => {
  const resultById = new Map(results.map((r) => [r.id, r]))
  let approvedCount = 0
  const failures: BulkFailureRow[] = []
  for (const c of sent) {
    const result = resultById.get(c.id)
    if (result?.kind === 'approved') {
      approvedCount += 1
      continue
    }
    const base = {
      id: c.id,
      title: c.title,
      requestedBy: c.requestedBy,
    }
    const kind =
      result === undefined ? 'other' : classifyBulkFailure(result.reason)
    switch (kind) {
      case 'violation': {
        const details = c.violationSections.flatMap((s) => s.details)
        const shown = details.slice(0, MAX_FAILURE_DETAIL_LINES)
        failures.push({
          ...base,
          lines:
            shown.length > 0
              ? shown
              : [
                  // 画面が算出しない軸 (成行不可・確認後の設定変更など) は、 単件エラーと
                  // 同じ文言を write_blocked_reason から引く。
                  (result?.kind === 'failed'
                    ? result.writeBlockedMessage
                    : undefined) ?? messages.violationFallback,
                ],
          hiddenCount: details.length - shown.length,
        })
        break
      }
      case 'updatedByOther':
        failures.push({
          ...base,
          lines: [messages.updatedByOther],
          hiddenCount: 0,
        })
        break
      case 'canceledOrDeleted':
        failures.push({
          ...base,
          lines: [messages.canceledOrDeleted],
          hiddenCount: 0,
        })
        break
      case 'gateClosure':
        failures.push({
          ...base,
          lines: [messages.gateClosure],
          hiddenCount: 0,
        })
        break
      case 'other':
        failures.push({ ...base, lines: [messages.other], hiddenCount: 0 })
        break
    }
  }
  return { approvedCount, failures }
}
